import * as React from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { Eye, EyeOff, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/lib/auth"
import { isFirebaseConfigured } from "@/lib/firebase"
import { USE_MOCKS } from "@/lib/api"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

import {
  CruceDePortada,
  GoogleMark,
  Portada,
  Separador,
  traducirError,
} from "@/routes/login/Portada"

/**
 * ENTRAR. Sólo entrar.
 *
 * Es la mitad de un par: esta página es para quien YA tiene un sitio adonde
 * entrar, y `RegistroPage` para quien viene a crearlo. Cesar: «deberías tener
 * dos páginas, registro y log in, que cumplen roles diferentes».
 *
 * ===========================================================================
 * POR QUÉ ERAN DOS COSAS EN UNA, Y POR QUÉ ESO SE PAGA
 * ===========================================================================
 *
 * Había una sola pantalla que hacía de portada del producto Y de formulario de
 * entrada: el identificador enorme, un titular, un párrafo explicando lo que
 * hace Marty, la presentación en primera persona, el botón, y un descargo
 * legal. Seis bloques, y el trabajo de la pantalla —meter a alguien dentro—
 * empezaba en el cuarto.
 *
 * Quien abre el login **ya sabe qué es Marty**: viene de un enlace, de un
 * marcador o de que le echaron por caducidad de sesión. Explicarle el producto
 * ahí es hablarle del restaurante mientras espera en la puerta. Todo eso es de
 * la web, o del registro, que es donde alguien sí está decidiendo.
 *
 * Lo que queda es: quién eres (el identificador), dónde estás («Iniciar
 * sesión»), y la puerta. En ese orden y nada más.
 *
 * ---------------------------------------------------------------------------
 * EL BOTÓN DE GOOGLE ES EL PRINCIPAL, Y NO POR ESTÉTICA
 *
 * En este proyecto de Firebase **el ingreso por contraseña está deshabilitado**:
 * el formulario de correo no lleva a ningún sitio para casi nadie. Por eso
 * Google va primero y con su marca de cuatro colores, y el correo queda detrás
 * de un separador.
 *
 * El formulario NO se esconde tras un enlace, y eso cambió respecto de la
 * versión anterior: plegarlo ahorraba tres centímetros y le costaba un clic a
 * todo el que sí lo usa —desarrollo, las pruebas de navegador, y cualquier
 * instalación donde Google no sea el camino—. Con el resto del texto fuera, cabe
 * entero sin pelearse con nada.
 */
export function LoginPage() {
  const { profile, loading, signInWithEmail, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [verClave, setVerClave] = React.useState(false)
  const [pending, setPending] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  /*
    LA RAÍZ, Y NO UNA PANTALLA CONCRETA. El destino por defecto lo decide
    `Entrada`: mira si queda algo del traspaso y manda a primeros pasos o al
    dashboard. Ver `routes/onboarding/Entrada.tsx`.
  */
  const from = (location.state as { from?: string } | null)?.from ?? "/"

  if (!loading && profile) return <Navigate to={from} replace />

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      await signInWithEmail(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(traducirError(err))
    } finally {
      setPending(false)
    }
  }

  async function handleGoogle() {
    setPending(true)
    setError(null)
    try {
      await signInWithGoogle()
      navigate(from, { replace: true })
    } catch (err) {
      setError(traducirError(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Portada
      titulo={t("Iniciar sesión")}
      error={error}
      pie={
        <>
          <CruceDePortada
            pregunta={t("¿Todavía no tienes cuenta?")}
            accion={t("Da de alta tu empresa")}
            a="/registro"
          />
          {!isFirebaseConfigured ? (
            <p className="mt-5 rounded-md border border-warning/35 bg-warning-soft p-2.5 text-2xs leading-4">
              {t("Firebase no está configurado (faltan las variables")}{" "}
              <code className="code">{t("VITE_FIREBASE_*")}</code>).
              {USE_MOCKS
                ? " Con VITE_USE_MOCKS activo puedes entrar igual: la sesión es local y los datos son de ejemplo."
                : " Configura las variables para poder iniciar sesión."}
            </p>
          ) : null}
        </>
      }
    >
      <Button
        size="lg"
        className="h-11 w-full justify-center gap-2.5 text-sm"
        onClick={handleGoogle}
        disabled={pending}
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleMark className="h-4 w-4" />
        )}
        {t("Continuar con Google")}
      </Button>

      <Separador>{t("o")}</Separador>

      <form onSubmit={handleSubmit} className="space-y-3.5">
        <div className="space-y-1.5">
          <Label htmlFor="email">{t("Correo")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required={isFirebaseConfigured}
            placeholder={t("contador@estudio.pe")}
            className="h-10"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">{t("Contraseña")}</Label>
          {/*
            EL OJO. Es lo que evita el tercer intento fallido en un teclado que
            no es el tuyo, y en un producto que además bloquea la cuenta por
            «demasiados intentos» eso no es comodidad.
          */}
          <div className="relative">
            <Input
              id="password"
              type={verClave ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required={isFirebaseConfigured}
              className="h-10 pr-10"
            />
            <button
              type="button"
              onClick={() => setVerClave((v) => !v)}
              aria-pressed={verClave}
              aria-label={verClave ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              title={verClave ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              className={cn(
                "absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-md",
                "text-muted-foreground transition-colors hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              {verClave ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          variant="outline"
          size="lg"
          className="h-10 w-full"
          disabled={pending}
        >
          {pending ? <Loader2 className="animate-spin" /> : null}
          {t("Entrar")}
        </Button>
      </form>
    </Portada>
  )
}
